import { Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { Button, ErrorBox } from "@tfg/ui"
import { zod4Resolver } from "mantine-form-zod-resolver"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import { LoginTextInput } from "~/pages/Auth/components/LoginTextInput"
import {
  getPasswordFormDisplayedError,
  type PasswordFormError,
} from "~/pages/Auth/utils/password-form-error"

import { LoginButton } from "./LoginButton"

const passwordSchema = z.object({
  username: standardEmail(),
  password: z.string().min(1, "Password is required"),
})

type PasswordFormProps = {
  onSubmit: (values: z.infer<typeof passwordSchema>) => Promise<void>
  onForgotPassword: () => void
  onUseMagicLink: () => void | Promise<void>
  canUseMagicLink: boolean
  isLoading: boolean
  isSendingMagicLink?: boolean
  error: PasswordFormError
  username: string
}

export const PasswordForm = ({
  onSubmit,
  onForgotPassword,
  onUseMagicLink,
  canUseMagicLink,
  isLoading,
  isSendingMagicLink = false,
  error,
  username,
}: PasswordFormProps) => {
  const { t } = useTranslation("account")
  const displayedError = getPasswordFormDisplayedError({
    error,
    invalidCredentialsMessage: t("login.errors.invalidCredentials"),
  })
  const form = useForm<z.infer<typeof passwordSchema>>({
    validate: zod4Resolver(passwordSchema),
    initialValues: {
      username,
      password: "",
    },
  })

  return (
    <>
      <ErrorBox error={displayedError} />
      <Stack align="right" w="100%" gap={0}>
        <LoginTextInput
          name="password"
          type="password"
          autoComplete="current-password"
          required
          autoFocus
          label={t("login.labels.password")}
          placeholder={t("login.placeholders.password")}
          onSubmit={() => form.onSubmit(onSubmit)()}
          {...form.getInputProps("password")}
          data-testid="login-password"
        />
        <Button
          w="fit-content"
          style={{ alignSelf: "end", textAlign: "right", justifySelf: "end" }}
          size="xs"
          variant="tertiary"
          fz="xxs"
          data-testid="login-forgot-password"
          disabled={isLoading || isSendingMagicLink}
          onClick={onForgotPassword}
        >
          {t("login.forgotPassword.button")}
        </Button>
      </Stack>
      <LoginButton
        loading={isLoading}
        disabled={isSendingMagicLink}
        data-testid="login-submit"
        onClick={() => form.onSubmit(onSubmit)()}
      >
        {t("login.submit")}
      </LoginButton>
      {canUseMagicLink ? (
        <Button
          size="sm"
          fullWidth={true}
          variant="tertiary"
          loading={isSendingMagicLink}
          disabled={isLoading || isSendingMagicLink}
          data-testid="login-use-magic-link"
          onClick={onUseMagicLink}
        >
          {t("login.password.useMagicLink")}
        </Button>
      ) : null}
    </>
  )
}
